import { localeHasCldrData, uiLocale } from "./locale";

// Local YYYY-MM-DD for any date sent to the API. A Date built at local midnight
// (e.g. new Date(2025, 8, 15)) shifts to the previous calendar day under
// toISOString() in UTC-negative locales — this formats from the LOCAL parts so
// the day never drifts. Use this everywhere instead of toISOString().split("T").
export const toLocalISODate = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

// Parse a "YYYY-MM-DD" string into a LOCAL Date (midnight local). `new Date(str)`
// parses date-only strings as UTC, which shifts the weekday in some locales —
// this keeps the calendar day intact. Returns null for empty/invalid input.
export const parseLocalISODate = (str) => {
  if (!str || typeof str !== "string") return null;
  const [y, m, d] = str.split("-").map(Number);
  if (!y || !m || !d) return null;
  const date = new Date(y, m - 1, d);
  return Number.isNaN(date.getTime()) ? null : date;
};

// Whole days from a "YYYY-MM-DD" date to today, both at LOCAL midnight so a DST
// boundary can't turn one day into 0.96 of one. Negative for a future date, 0
// for none.
export const daysLate = (isoDate) => {
  const due = parseLocalISODate(isoDate);
  if (!due) return 0;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((today - due) / 86400000);
};

// The opening value for a "pick a new day" field, given the day it currently
// holds. A past date comes back as today.
//
// It exists because every reschedule form in the app opens on the visit's
// EXISTING date, and the forms that matter most are reached from a visit whose
// date has already gone — the overdue queue offers no other kind. That put the
// field below its own `min={today}`: invalid before the user had touched it,
// with a browser refusing to submit and nothing on screen saying why. Both
// callers already pin `min` to today, so this is the same rule stated once on
// the value side rather than twice, differently.
export const notBeforeToday = (iso) => {
  const today = toLocalISODate(new Date());
  // Plain string compare: both sides are YYYY-MM-DD, which sorts as dates do.
  return iso && iso >= today ? iso : today;
};

// Intl formatters are costly to construct and toLocaleDateString built a fresh
// one on every call — these run per observation dot in the analytics grids. The
// cache is keyed by locale + option set, so a language switch simply misses it
// and builds a new formatter — nothing to invalidate.
const DATE_OPTIONS = {
  short: { month: "short", day: "numeric", year: "numeric" },
  monthDay: { month: "short", day: "numeric" },
  monthShort: { month: "short" },
  numericMonthDay: { month: "numeric", day: "numeric" },
  weekdayMonthDay: { weekday: "short", day: "numeric", month: "short" },
  dateTime: {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  },
  time: { hour: "2-digit", minute: "2-digit" },
};

// Kazakh is the app's default language and a runtime without full ICU has no
// data for it — see `localeHasCldrData`. There it formats from the ROOT locale
// with no error: "2026 M08 10". These tables are the stand-in, and they are
// only ever reached when the probe says the platform cannot do it itself.
// Kazakh takes no agreement after a numeral, so a plain template is correct.
const KK_MONTHS = ["қаң", "ақп", "нау", "сәу", "мам", "мау", "шіл", "там", "қыр", "қаз", "қар", "жел"];
const KK_WEEKDAYS = ["жс", "дс", "сс", "ср", "бс", "жм", "сн"]; // indexed by getDay()
const pad = (n) => String(n).padStart(2, "0");

const KK_FORMATTERS = {
  short: (d) => `${d.getDate()} ${KK_MONTHS[d.getMonth()]} ${d.getFullYear()}`,
  monthDay: (d) => `${d.getDate()} ${KK_MONTHS[d.getMonth()]}`,
  monthShort: (d) => KK_MONTHS[d.getMonth()],
  numericMonthDay: (d) => `${pad(d.getDate())}.${pad(d.getMonth() + 1)}`,
  weekdayMonthDay: (d) =>
    `${KK_WEEKDAYS[d.getDay()]}, ${d.getDate()} ${KK_MONTHS[d.getMonth()]}`,
  dateTime: (d) =>
    `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}, ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  time: (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}`,
};

const dateFormatters = new Map();
const dateFormatter = (style) => {
  const locale = uiLocale();
  const key = `${locale}:${style}`;
  let formatter = dateFormatters.get(key);
  if (!formatter) {
    formatter = localeHasCldrData(locale)
      ? new Intl.DateTimeFormat(locale, DATE_OPTIONS[style])
      : { format: KK_FORMATTERS[style] };
    dateFormatters.set(key, formatter);
  }
  return formatter;
};

// Unparseable input reached toLocaleDateString as "Invalid Date"; Intl.format
// throws on it, so it takes the same dash as empty input.
const formatWith = (style, input) => {
  if (!input) return "—";
  const date = input instanceof Date ? input : new Date(input);
  return Number.isNaN(date.getTime()) ? "—" : dateFormatter(style).format(date);
};

export const formatShortDate = (input) => formatWith("short", input);

// Month + day only ("Jun 1") — compact column headers for observation matrices.
export const formatMonthDay = (input) => formatWith("monthDay", input);

// The remaining shapes the app renders. They live here rather than as inline
// `toLocaleDateString(uiLocale(), {...})` calls at the five sites that used to
// hold them, because each of those sites was a place Kazakh broke on its own.
export const formatMonthShort = (input) => formatWith("monthShort", input);
export const formatNumericMonthDay = (input) => formatWith("numericMonthDay", input);
export const formatWeekdayMonthDay = (input) => formatWith("weekdayMonthDay", input);
export const formatDateTime = (input) => formatWith("dateTime", input);
export const formatTime = (input) => formatWith("time", input);

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const WEEK = 7 * DAY;

// Intl formatters are costly to construct and these run per row in the
// notification feed. The cache is keyed BY locale, so a language switch simply
// misses it and builds a new formatter — nothing to invalidate.
const KK_RELATIVE = {
  second: () => "қазір",
  minute: (n) => `${n} мин бұрын`,
  hour: (n) => `${n} сағ бұрын`,
  day: (n) => (n === 1 ? "кеше" : `${n} күн бұрын`),
};

const relativeFormatters = new Map();
const relativeFormatter = () => {
  const locale = uiLocale();
  let formatter = relativeFormatters.get(locale);
  if (!formatter) {
    formatter = localeHasCldrData(locale)
      ? new Intl.RelativeTimeFormat(locale, { numeric: "auto" })
      : { format: (value, unit) => KK_RELATIVE[unit](Math.abs(value)) };
    relativeFormatters.set(locale, formatter);
  }
  return formatter;
};

/**
 * "5 minutes ago" / "5 минут назад" / "5 минут бұрын".
 *
 * Delegated to Intl.RelativeTimeFormat rather than translated. This used to
 * build English by hand (`${m} minute${m === 1 ? "" : "s"} ago`), which is not
 * merely untranslated but unfixable by translation: Russian needs three plural
 * forms chosen by the last digit — 1 минуту, 2 минуты, 5 минут, 21 минуту — and
 * an i18n key per unit could not express that without duplicating CLDR.
 *
 * `numeric: "auto"` also buys idiomatic single-word forms the platform already
 * knows: "yesterday" / "вчера" / "кеше", and Russian even has "позавчера".
 */
export const formatRelativeTime = (input) => {
  if (!input) return "";
  const then = new Date(input).getTime();
  if (Number.isNaN(then)) return "";

  const diff = Date.now() - then;
  const format = relativeFormatter();
  // Negative values read as the past ("ago"); 0 seconds renders as "now".
  if (diff < MINUTE) return format.format(0, "second");
  if (diff < HOUR) return format.format(-Math.round(diff / MINUTE), "minute");
  if (diff < DAY) return format.format(-Math.round(diff / HOUR), "hour");
  if (diff < WEEK) return format.format(-Math.round(diff / DAY), "day");
  return formatShortDate(input);
};
